import React, { useState, useEffect } from 'react';
import type { TaskStatus, Task, TaskType, TeamMember } from '../../../../types';
import { TaskCard } from '../TaskCard/TaskCard';
import { DraggableTask } from '../DragAndDrop/DraggableTask';
import { DroppableZone } from '../DragAndDrop/DroppableZone';
import { AddIcon, UserIcon } from '../../../../components/ui/Icons';
import { QuickAddModal } from '../QuickAdd/QuickAddModal';
import { TeamMemberFilter } from '../TeamMemberFilter';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

// 待办列排序模式
type TodoSortMode = 'priority' | 'manual';

interface TaskColumnProps {
  status: TaskStatus;
  title: string;
  tasks: Task[];
  taskTypes: TaskType[];
  isCompact?: boolean;
  // 新增：接受过滤后的任务，优先使用这个属性
  filteredTasks?: Task[];
  // 团队成员相关属性
  teamMembers?: TeamMember[];
  selectedMemberId?: string | null;
  onMemberSelect?: (memberId: string | null) => void;
  isTeamWorkspace?: boolean;
}

const getStatusColor = (status: TaskStatus) => {
  switch (status) {
    case 'TODO':
      return 'bg-gray-400';
    case 'IN_PROGRESS':
      return 'bg-yellow-400';
    case 'REVIEW':
      return 'bg-amber-400';
    case 'BLOCKED':
      return 'bg-red-400';
    case 'DONE':
      return 'bg-green-400';
    default:
      return 'bg-gray-400';
  }
};

const getStatusCount = (tasks: Task[], status: TaskStatus) => {
  return tasks.filter(task => task.status === status).length;
};

export const TaskColumn: React.FC<TaskColumnProps> = ({
  status,
  title,
  tasks,
  taskTypes,
  isCompact = false,
  filteredTasks,
  teamMembers = [],
  selectedMemberId,
  onMemberSelect,
  isTeamWorkspace = false
}) => {
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showMemberFilter, setShowMemberFilter] = useState(false);

  // 待办列排序模式状态（从 localStorage 读取，默认为手动排序）
  const [todoSortMode, setTodoSortMode] = useState<TodoSortMode>(() => {
    if (status !== 'TODO') return 'manual'; // 非待办列不需要此状态
    const saved = localStorage.getItem('todoSortMode');
    return (saved as TodoSortMode) || 'manual';
  });

  // 保存排序模式到 localStorage
  useEffect(() => {
    if (status === 'TODO') {
      localStorage.setItem('todoSortMode', todoSortMode);
    }
  }, [todoSortMode, status]);

  // 切换排序模式
  const toggleSortMode = () => {
    setTodoSortMode(prev => prev === 'priority' ? 'manual' : 'priority');
  };

  // 如果提供了过滤后的任务，使用过滤后的任务，否则使用原来的逻辑
  let statusTasks = filteredTasks || tasks.filter(task => task.status === status);
  const taskCount = statusTasks.length;

  // 按状态区分排序逻辑
  let sortedTasks = [...statusTasks].sort((a, b) => {
    if (status === 'TODO') {
      if (todoSortMode === 'priority') {
        // 优先级模式：按优先级排序，同优先级内按创建时间倒序
        const priorityOrder = { HIGH: 0, MEDIUM: 1, LOW: 2 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      } else {
        // 手动模式：按 order 降序排列（order 大的在前面）
        return b.order - a.order;
      }
    } else if (status === 'DONE') {
      // 已完成任务：按完成时间倒序排列（最新完成的在前）
      return new Date(b.completedAt || b.updatedAt).getTime() - new Date(a.completedAt || a.updatedAt).getTime();
    } else if (status === 'REVIEW') {
      // 待验收：等得最久的排在最前
      return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    } else {
      // 进行中任务：按 order 升序排列
      return a.order - b.order;
    }
  });

  // 已完成任务：只显示最近10天完成的任务
  if (status === 'DONE') {
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);

    sortedTasks = sortedTasks.filter(task => {
      const completionDate = new Date(task.completedAt || task.updatedAt);
      return completionDate >= tenDaysAgo;
    });
  }

  // 获取任务类型
  const getTaskType = (taskTypeId: string) => {
    return taskTypes.find(type => type.id === taskTypeId);
  };

  // 时间分组工具函数
  const getTimeGroup = (dateString?: string) => {
    if (!dateString) return 'thisweek';
    
    const taskDate = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // 获取本周开始日期（周一）
    const weekStart = new Date(today);
    const dayOfWeek = today.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    weekStart.setDate(today.getDate() - daysToMonday);
    
    const taskDateOnly = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
    
    if (taskDateOnly.getTime() === today.getTime()) {
      return 'today';
    } else if (taskDateOnly.getTime() === yesterday.getTime()) {
      return 'yesterday';
    } else if (taskDateOnly >= weekStart && taskDateOnly < today) {
      return 'thisweek';
    } else {
      return 'thisweek';
    }
  };

  // 按时间分组任务
  const groupTasksByTime = (tasks: Task[]) => {
    const groups = {
      today: [] as Task[],
      yesterday: [] as Task[],
      thisweek: [] as Task[]
    };
    
    tasks.forEach(task => {
      // 使用任务的更新时间作为完成时间，如果没有则使用创建时间
      const completionDate = task.updatedAt || task.createdAt;
      const timeGroup = getTimeGroup(completionDate);
      if (groups[timeGroup as keyof typeof groups]) {
        groups[timeGroup as keyof typeof groups].push(task);
      }
    });
    
    // 按order排序，保持用户拖拽的顺序
    Object.keys(groups).forEach(key => {
      groups[key as keyof typeof groups].sort((a, b) => a.order - b.order);
    });
    
    return groups;
  };

  return (
    <div className={`bg-neutral-25 shadow-sm border border-neutral-100 flex flex-col min-h-[calc(100vh-15rem)] ${
      status === 'TODO' ? 'rounded-tl-xl rounded-tr-xl' : 'rounded-t-xl'
    } relative`}>
      {/* 列头 */}
      <div className={`p-4 ${
        status === 'TODO' ? 'rounded-tl-xl rounded-tr-xl' : 'rounded-t-xl'
      } ${
        status === 'IN_PROGRESS' ? 'bg-gradient-to-r from-blue-50 to-blue-100 border-b border-neutral-100' :
        status === 'REVIEW' ? 'bg-gradient-to-r from-amber-50 to-amber-100 border-b border-neutral-100' :
        status === 'BLOCKED' ? 'bg-gradient-to-r from-red-50 to-red-100 border-b border-neutral-100' :
        status === 'DONE' ? 'bg-gradient-to-r from-green-50 to-green-100 border-b border-neutral-100' :
        'border-b border-neutral-100'
      }`}>
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-700 flex items-center whitespace-nowrap">
            <span className={`w-3 h-3 ${getStatusColor(status)} rounded-full mr-3`}></span>
            {title}
            <span className={`
              ml-2 px-2 py-1 text-xs rounded-full
              ${status === 'TODO' ? 'bg-gray-100 text-gray-600' : ''}
              ${status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-600' : ''}
              ${status === 'REVIEW' ? 'bg-amber-100 text-amber-700' : ''}
              ${status === 'BLOCKED' ? 'bg-red-100 text-red-600' : ''}
              ${status === 'DONE' ? 'bg-green-100 text-green-600' : ''}
            `}>
              {taskCount}
            </span>
          </h2>

          <div className="flex items-center gap-2 whitespace-nowrap">
            {/* 待办列排序模式切换按钮 */}
            {status === 'TODO' && (
              <button
                onClick={toggleSortMode}
                className={`
                  px-2 py-1 text-xs rounded-md font-medium transition-all duration-200
                  ${todoSortMode === 'priority'
                    ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }
                `}
                title={`当前：${todoSortMode === 'priority' ? '优先级排序' : '手动排序'}，点击切换`}
              >
                {todoSortMode === 'priority' ? '优先级' : '手动'}
              </button>
            )}

            {/* 团队成员过滤按钮 - 只在团队空间的进行中和完成列显示 */}
            {isTeamWorkspace && (status === 'IN_PROGRESS' || status === 'DONE') && teamMembers.length > 0 && (
              <button
                onClick={() => setShowMemberFilter(!showMemberFilter)}
                className={`p-1.5 rounded transition-colors ${
                  showMemberFilter || selectedMemberId
                    ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title="按团队成员过滤"
              >
                <UserIcon size={16} />
              </button>
            )}

            {/* 添加任务按钮和快捷键提示 */}
            {status === 'TODO' && (
              <>
                <button
                  onClick={() => setShowQuickAdd(true)}
                  className="text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors flex items-center gap-1"
                  title="添加新任务"
                >
                  <AddIcon size={14} />
                  <span className="hidden 2xl:inline">添加</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 团队成员过滤器 - 放在列的右侧，从列头下方开始 */}
      {isTeamWorkspace && (status === 'IN_PROGRESS' || status === 'DONE') && (
        <TeamMemberFilter
          teamMembers={teamMembers}
          selectedMemberId={selectedMemberId}
          onMemberSelect={onMemberSelect || (() => {})}
          isVisible={showMemberFilter}
        />
      )}

      {/* 任务列表 */}
      <div className="flex-1 min-h-[400px]">
        {status === 'TODO' ? (
          // 待办任务按创建时间平铺显示
          <DroppableZone
            id={status}
            status={status}
            className="h-full min-h-[400px] relative"
          >
            <div className="p-4 space-y-2">
              <SortableContext
                items={sortedTasks.map(task => task.id)}
                strategy={verticalListSortingStrategy}
              >
                {sortedTasks.length > 0 ? (
                  sortedTasks.map(task => (
                    <DraggableTask
                      key={task.id}
                      task={task}
                      taskType={getTaskType(task.taskTypeId)}
                      isCompact={isCompact}
                    />
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <div className="mb-2">暂无任务</div>
                    <div className="text-xs">点击"添加"按钮创建新任务</div>
                  </div>
                )}
              </SortableContext>
            </div>
          </DroppableZone>
        ) : status === 'DONE' ? (
          // 已完成任务按时间分组显示
          <DroppableZone
            id={`${status}-main`}
            status={status}
            className="h-full min-h-[400px] relative"
          >
            <div>
            {sortedTasks.length > 0 ? (() => {
              const timeGroups = groupTasksByTime(sortedTasks);
              return (
                <>
                  {/* 今天完成的任务区域 */}
                  {timeGroups.today.length > 0 && (
                    <DroppableZone
                      id={`${status}-today`}
                      status={status}
                      className="time-zone today-zone bg-neutral-50/40 relative"
                    >
                      <div className="absolute top-2 right-3 flex items-center gap-1 text-xs text-gray-600">
                        <span className="font-medium">今天</span>
                        <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full font-medium">
                          {timeGroups.today.length}
                        </span>
                      </div>
                      <SortableContext 
                        items={timeGroups.today.map(task => task.id)} 
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="priority-content">
                          <div className="pt-6 pb-2 px-1 space-y-2">
                            {timeGroups.today.map(task => (
                              <DraggableTask
                                key={task.id}
                                task={task} 
                                taskType={getTaskType(task.taskTypeId)}
                                isCompact={isCompact}
                              />
                            ))}
                          </div>
                        </div>
                      </SortableContext>
                    </DroppableZone>
                  )}

                  {/* 昨天完成的任务区域 */}
                  {timeGroups.yesterday.length > 0 && (
                    <DroppableZone
                      id={`${status}-yesterday`}
                      status={status}
                      className="time-zone yesterday-zone bg-neutral-50/40 relative"
                    >
                      <div className="absolute top-2 right-3 flex items-center gap-1 text-xs text-gray-600">
                        <span className="font-medium">昨天</span>
                        <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full font-medium">
                          {timeGroups.yesterday.length}
                        </span>
                      </div>
                      <SortableContext 
                        items={timeGroups.yesterday.map(task => task.id)} 
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="priority-content">
                          <div className="pt-6 pb-2 px-1 space-y-2">
                            {timeGroups.yesterday.map(task => (
                              <DraggableTask
                                key={task.id}
                                task={task} 
                                taskType={getTaskType(task.taskTypeId)}
                                isCompact={isCompact}
                              />
                            ))}
                          </div>
                        </div>
                      </SortableContext>
                    </DroppableZone>
                  )}

                  {/* 本周完成的任务区域 */}
                  {timeGroups.thisweek.length > 0 && (
                    <DroppableZone
                      id={`${status}-thisweek`}
                      status={status}
                      className="time-zone thisweek-zone bg-neutral-50/40 relative"
                    >
                      <div className="absolute top-2 right-3 flex items-center gap-1 text-xs text-gray-600">
                        <span className="font-medium">本周</span>
                        <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full font-medium">
                          {timeGroups.thisweek.length}
                        </span>
                      </div>
                      <SortableContext 
                        items={timeGroups.thisweek.map(task => task.id)} 
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="priority-content">
                          <div className="pt-6 pb-2 px-1 space-y-2">
                            {timeGroups.thisweek.map(task => (
                              <DraggableTask
                                key={task.id}
                                task={task} 
                                taskType={getTaskType(task.taskTypeId)}
                                isCompact={isCompact}
                              />
                            ))}
                          </div>
                        </div>
                      </SortableContext>
                    </DroppableZone>
                  )}
                </>
              );
            })() : (
              <DroppableZone
                id={status}
                status={status}
                className="p-1 h-full min-h-[300px]"
              >
                <div className="empty-state">
                  <div className="text-center py-12 text-gray-400">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" className="mx-auto mb-3 opacity-30">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <p className="text-sm">还没有完成的任务</p>
                    <p className="text-xs mt-1">加油完成你的第一个任务吧！</p>
                  </div>
                </div>
              </DroppableZone>
            )}
            </div>
          </DroppableZone>
        ) : (
          // 进行中任务正常显示
          <DroppableZone
            id={status}
            status={status}
            className="h-full min-h-[400px] relative"
          >
            <div className="p-4 space-y-2">
              <SortableContext
                items={sortedTasks.map(task => task.id)}
                strategy={verticalListSortingStrategy}
              >
                {sortedTasks.length > 0 ? (
                  sortedTasks.map(task => (
                    <DraggableTask
                      key={task.id}
                      task={task}
                      taskType={getTaskType(task.taskTypeId)}
                      isCompact={isCompact}
                    />
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    {status === 'REVIEW' ? (
                      <>
                        <div className="mb-2">没有待验收的任务</div>
                        <div className="text-xs">agent 做完的任务会出现在这里等你检查</div>
                      </>
                    ) : status === 'BLOCKED' ? (
                      <div className="mb-2">没有受阻的任务</div>
                    ) : (
                      <>
                        <div className="mb-2">暂无进行中任务</div>
                        <div className="text-xs">从待办任务中开始一个任务</div>
                      </>
                    )}
                  </div>
                )}
              </SortableContext>
            </div>
          </DroppableZone>
        )}
      </div>
      
      {/* 快速添加任务弹窗 */}
      <QuickAddModal
        isOpen={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        status={status}
      />
    </div>
  );
};